import React from 'react';
import _ from 'lodash';

import { shuffleArray } from 'utils';
import Card from './card';
import { QuizQuestions_quiz_master_quizzes_by_pk_questions as QuizQuestions } from 'graphql/queries/types/QuizQuestions';
import { Question } from 'types';
import cloneDeep from 'lodash/cloneDeep';

export interface CardData {
    points: string; // grid card label
    question?: Question;
    shipTeam?: string; // ship owner team name 
    answered?: boolean;
}
export interface GridData {
    cardRows: CardData[];
}

interface Props {
    gridData: GridData[];
    questions: QuizQuestions[];
    eventLogo?: string | null;
    isSinglePlayer: boolean;
    maxQuestionDuration?: number | null;
    onCorrectAnswer: (gridItem: CardData) => void;
    onIncorrectAnswer: (gridItem: CardData) => void;
}

interface State {
    windowWidth: number;
    windowHeight: number;
    rows: number;
    cols: number;
    cardColumns: GridData[];
    localQuestions: Question[];
    failedQuestions: Question[];
}

export class Board extends React.Component<Props, State> {
    // TODO - save state to localStorage for a fast regeneration and to avoid losing progress mid quiz
    constructor(props: Props) {
        super(props);
        this.state = {
            windowWidth: window.innerWidth,
            windowHeight: window.innerHeight,
            cardColumns: [],
            rows: 0,
            cols: 0,
            localQuestions: [],
            failedQuestions: []
        };
    }

    handleResize() {
        this.setState({
            windowWidth: window.innerWidth,
            windowHeight: window.innerHeight
        });
    }

    componentDidMount() {
        window.addEventListener('resize', this.handleResize.bind(this));
        const rows = this.countRows(this.props.gridData);
        const localQuestions: Question[] = cloneDeep(this.props.questions);
        const cardColumns = this.defineQuestions(this.props.gridData, localQuestions, []);
        this.setState({
            cardColumns,
            rows,
            cols: this.props.gridData.length,
            localQuestions
        });
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
    }

    handleIncorrectAnswer(column: number, row: number) {
        // use timeout to give time for the animation to complete
        setTimeout(() => {
            const failedQuestion = this.state.cardColumns[column].cardRows[row];
            const localQuestions = [...this.state.localQuestions];
            const failedQuestions = [...this.state.failedQuestions];
            if (this.props.onIncorrectAnswer) {
                this.props.onIncorrectAnswer(failedQuestion);
            }

            const existsInFailed =
                this.state.failedQuestions.filter((fq) => fq.id === failedQuestion.question!.id)
                    .length > 0;
            if (!existsInFailed) {
                // remove the failed question from the question pool
                _.remove(localQuestions, (q: Question) => q.id === failedQuestion.question!.id);

                // add failed question to the failed questions pool
                failedQuestions.push(cloneDeep(failedQuestion.question!));
            }

            // define new questions for cards
            const cardColumns = this.defineQuestions(this.state.cardColumns, localQuestions, failedQuestions);
            this.setState({
                cardColumns,
                localQuestions,
                failedQuestions
            });
        }, 1000);
    }

    handleQuestionAnswered(column: number, row: number) {
        // use timeout to give time for the animation to complete
        setTimeout(() => {
            var newData = [...this.state.cardColumns];
            newData[column].cardRows[row].answered = true;
            const localQuestions = [...this.state.localQuestions];
            const answeredQuestion = this.state.cardColumns[column].cardRows[row];
            if (this.props.onCorrectAnswer) {
                this.props.onCorrectAnswer(answeredQuestion);
            }
            // remove the answered question from the question pool
            _.remove(localQuestions, (q: Question) => q.id === answeredQuestion.question!.id);

            // if we used a failed question to fill in the needed number of questions remove it from the failed pool
            const failedQuestions = [...this.state.failedQuestions];
            _.remove(failedQuestions, (q: Question) => q.id === answeredQuestion.question!.id);

            // define new questions for cards
            const cardColumns = this.defineQuestions(newData, localQuestions, failedQuestions);
            this.setState({
                cardColumns,
                localQuestions,
                failedQuestions
            });
        }, 1000);
    }

    render() {
        let headerHeight = this.state.windowWidth > 640 ? 60 : 32,
            cardWidth = this.state.windowWidth / this.state.cols,
            cardHeight = (this.state.windowHeight - headerHeight) / this.state.rows,
            cardComponents: JSX.Element[] = [];

        this.state.cardColumns.forEach((columnData, columnDataIndex) => {
            let left = columnDataIndex * cardWidth;
            columnData.cardRows.forEach((cardData, cardIndex) => {
                cardComponents.push(
                    <Card
                        onIncorrectAnswer={this.handleIncorrectAnswer.bind(this)}
                        onCorrectAnswer={this.handleQuestionAnswered.bind(this)}
                        column={columnDataIndex}
                        row={cardIndex}
                        left={left}
                        top={cardIndex * cardHeight + headerHeight}
                        height={cardHeight}
                        width={cardWidth}
                        cardData={cardData}
                        key={columnDataIndex + '-' + cardIndex}
                        eventLogo={this.props.eventLogo}
                        isSinglePlayer={this.props.isSinglePlayer}
                        maxQuestionDuration={this.props.maxQuestionDuration}
                    />
                );
            });
        });
        return cardComponents;
    }

    countRows(gridData: GridData[]) {
        let rows = 0;
        gridData.forEach((columnData) => {
            if (columnData.cardRows.length > rows) {
                rows = columnData.cardRows.length;
            }
        });
        return rows;
    }

    defineQuestions(cardData: GridData[], remainingQuestions: Question[], failedQuestions: Question[]): GridData[] {
        const cards: GridData[] = [];
        const rows = this.countRows(this.props.gridData);
        const columns = this.props.gridData.length;
        let lRemainingQuestions = [...remainingQuestions];
        shuffleArray(lRemainingQuestions);

        const answeredCount = _.filter(_.flatten(cardData.map((c) => c.cardRows)), 'answered').length;
        const neededQuestions = rows * columns - answeredCount;
        let questions: Question[] = [];

        if (neededQuestions <= lRemainingQuestions.length) {
            questions = _.slice(lRemainingQuestions, 0, neededQuestions);
        } else {
            // reuse failed questions when we don't have enough new ones
            // only add the missing number of questions.
            const reuseFailedCount = neededQuestions - lRemainingQuestions.length;
            const lFailedQuestions = [...failedQuestions];
            shuffleArray(lFailedQuestions);
            questions = [...lRemainingQuestions, ..._.slice(lFailedQuestions, 0, reuseFailedCount)];
        }

        shuffleArray(questions);

        let questionIndex = 0;
        cardData.forEach((column, cIndex) => {
            const cardColumns: CardData[] = [];
            column.cardRows.forEach((card) => {
                if (card.answered) {
                    cardColumns.push({ ...card });
                } else {
                    cardColumns.push({ ...card, question: cloneDeep(questions[questionIndex]) });
                    questionIndex++;
                }
            });
            cards[cIndex] = { cardRows: cardColumns };
        });

        return cards;
    }
}
